import { useEffect, useRef, useState } from "react";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ShieldCheck, Loader2, Sparkles, ArrowRight, VolumeX } from "lucide-react";
import logoImg from "@assets/Padpulse_logo_horizontal_nobg.png";
import welcomePoster from "@assets/welcome-poster.jpg";
import { type ExplainerVideo } from "@shared/schema";
import { usePageTitle } from "@/hooks/use-page-title";
import { needsPaymentSetupFromResponse } from "@shared/subscription-gate";
import { PADPULSE_PROMISE } from "@shared/padpulse-promise";
import { markOnboardingJustCompleted } from "@/lib/onboarding-signal";

interface MeUser {
  id: number;
  email: string;
  firstName?: string | null;
  phone?: string | null;
  onboardingCompletedAt?: string | null;
  onboardingWizardCompletedAt?: string | null;
  journeyStage?: string | null;
  status?: string;
  approved?: boolean;
  hasPaymentMethod?: boolean;
  needsPaymentSetup?: boolean;
  tosAcknowledgedAt?: string | null;
}

function getLoomEmbedUrl(url: string): string {
  try {
    const parsed = new URL(url);
    if (parsed.hostname.includes("loom.com")) {
      const path = parsed.pathname.replace("/share/", "/embed/").replace("/v/", "/embed/");
      return `https://www.loom.com${path}`;
    }
  } catch {
  }
  return url;
}

export default function AppWelcome() {
  const queryClient = useQueryClient();
  usePageTitle("Welcome");
  // Set by the Stripe hosted-checkout success redirect.
  const checkoutSuccess =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("checkout") === "success";
  const checkoutSessionId =
    checkoutSuccess && typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("session_id")
      : null;
  // While the server confirms the just-completed Checkout session, hold the
  // payment-step redirect: the cached auth/me still says "needs payment" until
  // the new subscription is persisted (the webhook may not have landed yet).
  const [confirmingCheckout, setConfirmingCheckout] = useState<boolean>(!!checkoutSessionId);
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [isMuted, setIsMuted] = useState(true);
  const [showUnmuteHint, setShowUnmuteHint] = useState(true);
  const unmuteHintTimerRef = useRef<number | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    return () => {
      if (unmuteHintTimerRef.current) {
        window.clearTimeout(unmuteHintTimerRef.current);
        unmuteHintTimerRef.current = null;
      }
    };
  }, []);

  const meQuery = useQuery<{ user: MeUser }>({ queryKey: ["/api/app/auth/me"] });

  const confirmStartedRef = useRef(false);
  useEffect(() => {
    if (!checkoutSessionId || confirmStartedRef.current) return;
    confirmStartedRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        await apiRequest("POST", "/api/app/billing/checkout/confirm", { sessionId: checkoutSessionId });
      } catch (err) {
        // Non-fatal: the checkout.session.completed webhook persists the
        // subscription too. Log and fall through to a fresh auth/me read.
        console.warn("[welcome] checkout confirm failed", err);
      }
      await queryClient.invalidateQueries({ queryKey: ["/api/app/auth/me"] });
      if (!cancelled) setConfirmingCheckout(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [checkoutSessionId, queryClient]);

  const welcomeVideoQuery = useQuery<ExplainerVideo | null>({
    queryKey: ["/api/explainer-videos", "welcome"],
    queryFn: async () => {
      const res = await fetch("/api/explainer-videos/welcome", { credentials: "include" });
      if (!res.ok) return null;
      return res.json();
    },
    retry: false,
    staleTime: 60000,
  });

  const welcomeVideo = welcomeVideoQuery.data ?? null;
  const isVideoQueryLoading = welcomeVideoQuery.isLoading;

  const loomEmbedUrl =
    !isVideoQueryLoading && welcomeVideo?.embedUrl
      ? getLoomEmbedUrl(welcomeVideo.embedUrl)
      : null;

  useEffect(() => {
    if (!meQuery.data) return;
    const u = meQuery.data.user;
    if (!u) {
      navigate("/app/login");
      return;
    }
    // Hold EVERY redirect (dashboard included) until the Checkout session is
    // confirmed and /me refetched — otherwise AppLayout sees the stale
    // "needs payment" state and bounces the owner back to payment.
    if (confirmingCheckout || meQuery.isFetching) return;
    if (u.onboardingWizardCompletedAt) {
      navigate("/app/dashboard");
      return;
    }
    if (!u.phone) {
      navigate("/app/onboarding");
      return;
    }
    const isBillableAccountOwner =
      (meQuery.data as any)?.isBillableAccountOwner ??
      !(meQuery.data as any)?.activeMembership;
    if (
      (u.approved || u.status === "active") &&
      u.tosAcknowledgedAt &&
      // Modular owners need a live subscription (Checkout), not just a card.
      needsPaymentSetupFromResponse(u) &&
      isBillableAccountOwner
    ) {
      navigate("/app/onboarding/payment");
      return;
    }
  }, [meQuery.data, meQuery.isFetching, confirmingCheckout, navigate]);

  const completeMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/onboarding/complete", {});
      return res.json();
    },
    onSuccess: async () => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      await queryClient.invalidateQueries({ queryKey: ["/api/app/auth/me"] });
      await queryClient.refetchQueries({ queryKey: ["/api/app/auth/me"] });
      // Task #3971 — Signal the dashboard's Team Inbox to default to "Assigned
      // to me" on this first post-onboarding load so the seeded setup checklist
      // is front and center.
      markOnboardingJustCompleted();
      navigate("/app/dashboard");
    },
    onError: () => {
      toast({ title: "Something went wrong", description: "Please try again.", variant: "destructive" });
    },
  });

  const handleContinue = () => {
    completeMutation.mutate();
  };

  if (meQuery.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <Loader2 className="h-6 w-6 animate-spin text-brand" />
      </div>
    );
  }

  const firstName = meQuery.data?.user?.firstName?.trim() || "";

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-72 h-72 bg-blue-100/40 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-orange-100/30 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-3xl">
        <div className="mb-6 flex justify-center">
          <img src={logoImg} alt="PadPulse" className="h-[50px] w-auto" data-testid="img-welcome-logo" />
        </div>

        <Card className="w-full p-6 sm:p-10 shadow-xl border border-gray-100 bg-white/90 backdrop-blur-sm">
          {checkoutSuccess && (
            <div
              className="mb-6 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-start gap-2"
              data-testid="banner-checkout-promise"
            >
              <ShieldCheck className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
              <p className="text-sm text-emerald-800">{PADPULSE_PROMISE.checkoutLine}</p>
            </div>
          )}
          <div className="flex items-center justify-center gap-2 text-brand mb-2">
            <Sparkles className="h-5 w-5" />
            <span className="text-sm font-medium uppercase tracking-wider">Welcome to PadPulse</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 text-center mb-2" data-testid="text-welcome-title">
            Welcome{firstName ? `, ${firstName}` : ""}!
          </h1>
          <p className="text-gray-600 text-center max-w-xl mx-auto mb-6" data-testid="text-welcome-subtitle">
            Watch the short intro video below, then head to your dashboard to get started.
          </p>

          {/* Video section: Loom embed (admin-configurable) or native MP4 fallback */}
          <div className="relative mb-6 overflow-hidden rounded-lg border border-gray-200 bg-black shadow-sm">
            {isVideoQueryLoading ? (
              <div className="relative" style={{ paddingTop: "56.25%" }}>
                <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                </div>
              </div>
            ) : loomEmbedUrl ? (
              <div className="relative" style={{ paddingTop: "56.25%" }}>
                <iframe
                  src={loomEmbedUrl}
                  frameBorder="0"
                  allowFullScreen
                  allow="autoplay; fullscreen; picture-in-picture"
                  className="absolute inset-0 w-full h-full"
                  title="Welcome Introduction"
                  data-testid="iframe-welcome-loom"
                />
              </div>
            ) : (
              <>
                <video
                  ref={videoRef}
                  src="/assets/welcome.mp4"
                  poster={welcomePoster}
                  controls
                  playsInline
                  preload="auto"
                  className="block h-auto w-full"
                  data-testid="video-welcome-intro"
                  onVolumeChange={() => {
                    const v = videoRef.current;
                    if (!v) return;
                    const muted = v.muted || v.volume === 0;
                    setIsMuted(muted);
                    if (!muted) {
                      setShowUnmuteHint(false);
                      if (unmuteHintTimerRef.current) {
                        window.clearTimeout(unmuteHintTimerRef.current);
                        unmuteHintTimerRef.current = null;
                      }
                    }
                  }}
                  onPlay={() => {
                    if (unmuteHintTimerRef.current) return;
                    if (!isMuted) return;
                    unmuteHintTimerRef.current = window.setTimeout(() => {
                      setShowUnmuteHint(false);
                      unmuteHintTimerRef.current = null;
                    }, 6000);
                  }}
                >
                  Your browser does not support the video tag.
                </video>
                {isMuted && showUnmuteHint && (
                  <button
                    type="button"
                    onClick={() => {
                      const v = videoRef.current;
                      if (!v) return;
                      v.muted = false;
                      if (v.volume === 0) v.volume = 1;
                      setIsMuted(false);
                      setShowUnmuteHint(false);
                      if (unmuteHintTimerRef.current) {
                        window.clearTimeout(unmuteHintTimerRef.current);
                        unmuteHintTimerRef.current = null;
                      }
                      v.play().catch(() => {});
                    }}
                    className="absolute top-3 right-3 z-10 flex items-center gap-2 rounded-full bg-black/70 px-3 py-2 text-xs font-semibold text-white shadow-lg backdrop-blur-sm transition-all hover:bg-black/85 focus:outline-none focus:ring-2 focus:ring-white/70"
                    aria-label="Unmute video"
                    data-testid="button-unmute-welcome-video"
                  >
                    <VolumeX className="h-4 w-4" />
                    <span>Tap to unmute</span>
                  </button>
                )}
                {isMuted && !showUnmuteHint && (
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute top-3 right-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black/50 text-white"
                    data-testid="indicator-muted-welcome-video"
                  >
                    <VolumeX className="h-4 w-4" />
                  </div>
                )}
              </>
            )}
          </div>

          <Button
            onClick={handleContinue}
            disabled={completeMutation.isPending}
            className="w-full bg-brand hover:bg-brand-hover text-white font-semibold h-12 text-base"
            data-testid="button-welcome-next"
          >
            {completeMutation.isPending ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <>
                Continue to Dashboard
                <ArrowRight className="h-4 w-4 ml-1.5" />
              </>
            )}
          </Button>
        </Card>
      </div>
    </div>
  );
}
